import { MutationFunction } from '@apollo/client'
import { Autocomplete, TextField } from '@mui/material'
import { FC, useState } from 'react'
import UpdatedText from 'shared/components/UpdatedText/UpdatedText'
import { useEffectOnlyAfterMount } from 'shared/hooks/useEffectAfterMount'
import { DropdownOption } from 'shared/models/dropdownOption'
import { getPxAsRem } from 'shared/utils'

const SECTOR_PREFERENCE_LIMIT = 3

interface SectorPreferences {
    sectorId: string
    sector: {
        id: string
        value: string
        bucket?: string | null
    }
    order: number
}

interface SectorProps {
    sectorOptions: DropdownOption[]
    updatedAt: string
    updatedBy: string
    candidateId?: string
    handleUpdate: MutationFunction
    sectorPreferences: SectorPreferences[]
}

const Sector: FC<SectorProps> = ({
    sectorPreferences,
    sectorOptions,
    updatedAt,
    updatedBy,
    handleUpdate,
    candidateId,
}) => {
    const [primarySectors, setPrimarySectors] = useState<DropdownOption[]>(
        sectorPreferences
            .filter((sector) => sector.order === 1)
            .map((sector) => sector.sector)
    )
    const [secondarySectors, setSecondarySectors] = useState<DropdownOption[]>(
        sectorPreferences
            .filter((sector) => sector.order === 2)
            .map((sector) => sector.sector)
    )
    const [tertiarySectors, setTertiarySectors] = useState<DropdownOption[]>(
        sectorPreferences
            .filter((sector) => sector.order === 3)
            .map((sector) => sector.sector)
    )

    const isOptionsDisabled = (option, selectedOptions): boolean => {
        return (
            selectedOptions?.length >= 3 &&
            !selectedOptions.some((item) => item.value === option.value)
        )
    }

    const handleSave = () => {
        handleUpdate({
            variables: {
                candidateId,
                preferences: [
                    ...primarySectors.map((sector) => ({
                        sectorId: sector.id,
                        order: 1,
                    })),
                    ...secondarySectors.map((sector) => ({
                        sectorId: sector.id,
                        order: 2,
                    })),
                    ...tertiarySectors.map((sector) => ({
                        sectorId: sector.id,
                        order: 3,
                    })),
                ],
            },
        })
    }

    useEffectOnlyAfterMount(() => {
        handleSave()
    }, [primarySectors, secondarySectors, tertiarySectors])

    return (
        <>
            <Autocomplete
                multiple
                fullWidth
                disableClearable
                sx={{ marginBottom: getPxAsRem(10) }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                    console.log(newValue)
                    if (newValue.length <= SECTOR_PREFERENCE_LIMIT)
                        setPrimarySectors(newValue)
                }}
                value={primarySectors}
                options={sectorOptions.filter(
                    (sector) =>
                        secondarySectors
                            .concat(tertiarySectors)
                            .map((sector) => sector.id)
                            .indexOf(sector.id) === -1
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Primary Sector"
                        placeholder=""
                    />
                )}
                getOptionDisabled={(option) =>
                    isOptionsDisabled(option, primarySectors)
                }
            />
            <Autocomplete
                multiple
                fullWidth
                disableClearable
                sx={{ marginBottom: getPxAsRem(10) }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                    if (newValue.length <= SECTOR_PREFERENCE_LIMIT)
                        setSecondarySectors(newValue)
                }}
                value={secondarySectors}
                options={sectorOptions.filter(
                    (sector) =>
                        primarySectors
                            .concat(tertiarySectors)
                            .map((sector) => sector.id)
                            .indexOf(sector.id) === -1
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Secondary Sector"
                        placeholder=""
                    />
                )}
                getOptionDisabled={(option) =>
                    isOptionsDisabled(option, secondarySectors)
                }
            />
            <Autocomplete
                multiple
                fullWidth
                disableClearable
                sx={{ marginBottom: getPxAsRem(10) }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                    if (newValue.length <= SECTOR_PREFERENCE_LIMIT)
                        setTertiarySectors(newValue)
                }}
                value={tertiarySectors}
                options={sectorOptions.filter(
                    (sector) =>
                        secondarySectors
                            .concat(primarySectors)
                            .map((sector) => sector.id)
                            .indexOf(sector.id) === -1
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Tertiary Sector"
                        placeholder=""
                    />
                )}
                getOptionDisabled={(option) =>
                    isOptionsDisabled(option, tertiarySectors)
                }
            />
            <UpdatedText updatedAt={updatedAt} updatedBy={updatedBy} />
        </>
    )
}

export default Sector
